<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import MultiselectDropdown from '../../../../../shared/components/ui/MultiselectDropdown.vue';

const props = defineProps({
  businessAccounts: {
    type: Array,
    default: () => [],
  },
  selectedBusinessAccountId: {
    type: [Number, String],
    default: null,
  },
  updateHistory: {
    type: Boolean,
    default: false,
  },
  ticketId: {
    type: [Number, String],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:selectedBusinessAccountId', 'change']);

const store = useStore();

const selectedBusinessAccount = computed(() => {
  return (
    props.businessAccounts.find(
      ba => ba.id === props.selectedBusinessAccountId
    ) || {}
  );
});

const handleRemoveBusinessAccount = async () => {
  if (!props.updateHistory || !props.ticketId) return;

  try {
    const response = await store.dispatch('tickets/removeBusinessAccount', {
      id: props.ticketId,
    });

    if (response) {
      emit('update:selectedBusinessAccountId', null);
      emit('change', null);
    }

    // Refresh the ticket history immediately after the change
    await store.dispatch('tickets/getTicketHistory', props.ticketId);
  } catch (error) {
    // Error removing business account - silently fail
  }
};

const handleBusinessAccountChange = async newBusinessAccount => {
  if (props.disabled) return;

  if (newBusinessAccount.id === props.selectedBusinessAccountId) {
    // If the same business account is selected, remove it
    emit('update:selectedBusinessAccountId', null);
    emit('change', null);

    // If updateHistory is true, also call the remove function
    if (props.updateHistory && props.ticketId) {
      await handleRemoveBusinessAccount();
    }
    return;
  }

  emit('update:selectedBusinessAccountId', newBusinessAccount.id);
  emit('change', newBusinessAccount);

  // If updateHistory is true, update the ticket history
  if (props.updateHistory && props.ticketId) {
    try {
      const response = await store.dispatch('tickets/changeBusinessAccount', {
        id: props.ticketId,
        newBusinessAccount: newBusinessAccount.id,
      });

      if (response && response.business_account_name) {
        store.commit('tickets/UPDATE_TICKET_BUSINESS_ACCOUNT', {
          id: props.ticketId,
          business_account_name: response.business_account_name,
          business_account_id: response.business_account_id,
        });
      }

      // Refresh the ticket history immediately after the change
      await store.dispatch('tickets/getTicketHistory', props.ticketId);
    } catch (error) {
      // Error updating business account - revert the change on error
      emit('update:selectedBusinessAccountId', props.selectedBusinessAccountId);
    }
  }
};
</script>

<template>
  <div>
    <div @click.prevent>
      <MultiselectDropdown
        :options="businessAccounts"
        :selected-item="selectedBusinessAccount"
        multiselector-title="Selecionar Conta Empresarial"
        multiselector-placeholder="Selecionar Conta Empresarial"
        input-placeholder="Buscar conta empresarial..."
        no-search-result="Nenhuma conta empresarial encontrada"
        :has-thumbnail="false"
        :disabled="disabled"
        @select="handleBusinessAccountChange"
      />
    </div>
  </div>
</template>
